import {
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { SystemPartner } from 'api/types';
import { useUserContext } from 'context/UserContextProvider';
import { defaultToastOptions } from 'helpers/manageErrorResponse';
import { useUnlinkSystempartnerMutation } from 'hooks/mutations/useUnlinkSystempartnerMutation';
import useWorkshopQuery from 'hooks/useWorkshopQuery';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FaUnlink } from 'react-icons/fa';

import { DangerZoneRow } from './DangerZoneRow';

type Props = {
  workshopId: number;
  systemPartnersOfWorkshop: SystemPartner[];
};

export const UnlinkSystempartner = ({ workshopId, systemPartnersOfWorkshop }: Props) => {
  const toast = useToast(defaultToastOptions);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user } = useUserContext();
  const unlinkSystempartnerMutation = useUnlinkSystempartnerMutation(workshopId);

  const disabledTooltip = useMemo<string | null>(() => {
    if (!user.permissions.includes('workshops:unlink-systempartner')) {
      return t('workshop:unlink_systempartner.tooltip.disabled.no_permission');
    }
    if (systemPartnersOfWorkshop.length === 0) {
      return t('workshop:unlink_systempartner.tooltip.disabled.no_systempartner');
    }
    return null;
  }, [systemPartnersOfWorkshop.length, t, user.permissions]);

  return (
    <>
      <DangerZoneRow>
        <Stack gap={0}>
          <Heading size="xxs" as="h3">
            {t('workshop:unlink_systempartner.title')}
          </Heading>
          <Text>
            {systemPartnersOfWorkshop.length > 0
              ? t('workshop:unlink_systempartner.default.body')
              : t('workshop:unlink_systempartner.no_systempartner.body')}
          </Text>
        </Stack>
        <Tooltip label={disabledTooltip} hasArrow>
          <Button leftIcon={<FaUnlink />} minWidth="24ch" colorScheme="red" onClick={onOpen} disabled={disabledTooltip != null}>
            {t('workshop:unlink_systempartner.default.button')}
          </Button>
        </Tooltip>
      </DangerZoneRow>

      <Modal size="xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('workshop:unlink_systempartner.modal.title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text pb="6">
              <Trans i18nKey="workshop:unlink_systempartner.modal.body" values={{ systemPartnerName: systemPartnersOfWorkshop.at(0)?.name }} />
            </Text>
          </ModalBody>

          <ModalFooter>
            <Button mr={3} onClick={onClose}>
              {t('workshop:unlink_systempartner.modal.button_secondary')}
            </Button>
            <Button
              variant="solid"
              colorScheme="red"
              onClick={() => {
                unlinkSystempartnerMutation.mutate(
                  {
                    systempartnerId: systemPartnersOfWorkshop.at(0)!.id,
                  },
                  {
                    onSuccess: () => {
                      queryClient.invalidateQueries({ queryKey: useWorkshopQuery.getKey(workshopId) });
                      toast({
                        title: t('workshop:unlink_systempartner.toast.success.title'),
                        status: 'success',
                      });
                    },
                    onError: (error) => {
                      toast({
                        title: error.message ?? t('errors:general'),
                        status: 'error',
                      });
                    },
                  },
                );
                onClose();
              }}
            >
              {t('workshop:unlink_systempartner.modal.button_primary')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
